import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Page, Group, Layout, Button, Grid, ExcelUpload, FormControl, Table, ErrorUnitProps } from "@/comn/components";
import {
    useForm,
    useToast,
    useFetch,
    useModal,
    useAuth,
    useStore,
    useGrid,
    usePopup,
    useResource,
    useExcel,
    usePage,
} from "@/comn/hooks";
import { BASE, URLS, APIS, SF_LPCO_INFO_U, SG_LPCO_HS_CODE_LIST } from "./services/OsciCfgnHscdService";
import { CommonErrors } from "@/comn/components";
import { lpcoUtils } from "@/tz/go/egaz/osci/lpco/comn";
import { OgaHsCdLst } from "@/tz/go/egaz/osci/lpco/comn/OgaHsCdLst";
import { multiply } from "lodash";

/*
 * @ 화면 컴포넌트 주석
 * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
 * @ !== ... ==! 치환 부호는 개발 완료 후 한글 주석을 인지하여 제거하기 위한 표시
 * @ 기본 여러줄의 주석 형태를 사용
 * @ 포맷
 * @ [화면명 영문]
 * @ [지환부호시작] [화면명 한글] [치환부호종료]
 *
 * 화면명 영문
 * !== 화면명 한글 ==!
 */

/*
 * Register LPCO information
 * !== LPCO 정보 등록 ==!
 */
export const OSCI0203029S = () => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0004"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    //    const { dclrNo } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const { openPopup } = usePopup();
    const [ogaHsCdList, setOgaHsCdList] = useState(comnUtils.getGridData([]));

    // ORG LPCO LIST
    const [orgLpcoList, setOrgLpcoList] = useState([]);

    useResource({
        defaultSchema: [{ area: "hsCd" }, { area: "comnCd", comnCd: "OSC0025" }, { area: "comnCd", comnCd: "OSC0113" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        lpcoInfoRgsr: useForm({
            defaultSchema: SF_LPCO_INFO_U,
            defaultValues: {
                orgCd: auth.get("orgCd"),
                orgNm: auth.get("orgNm"),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        ogaHsCdList: useGrid({
            defaultSchema: SG_LPCO_HS_CODE_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    const render = {
        grid_ogaHsCdList: {
            head: {},
            cell: {},
            edit: {
                hsCd: (props: any) => {
                    const { binding, rowValues, value } = props;
                    return (
                        <FormControl
                            type="text"
                            rightButton={{
                                icon: "search",
                                onClick: () => {
                                    modal.openModal({
                                        content: (
                                            <OgaHsCdLst
                                                params={{ orgCd: form.lpcoInfoRgsr.getValue("orgCd") }}
                                                callback={(data: any) => {
                                                    console.log("data ", data);
                                                    grid.ogaHsCdList.updateRow({
                                                        ...rowValues,
                                                        hsCd: data.code,
                                                        hsDesc: data.label,
                                                        appdStrtDt: data?.data?.appdStrtDt || "",
                                                        appdEndDt: data?.data?.appdEndDt || "",
                                                    });
                                                }}
                                            />
                                        ),
                                        size: "lg",
                                        callback: (data: any) => {
                                            console.log("data ", data);
                                        },
                                    });
                                },
                            }}
                            value={rowValues.hsCd}
                            onChange={(v) => {
                                grid.ogaHsCdList.updateRow({
                                    ...rowValues,
                                    hsCd: v,
                                });
                            }}
                        />
                    );
                },
            },
        },
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        saveOgaHscdList: useFetch({
            api: (data) => APIS.saveOgaHscdList({ ...data }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006" /**Processed successfully. */,
                    onConfirm: () => {
                        navigate(URLS.osci0203031s + "/" + data.appInfo?.content?.appFmId);
                    },
                    onCancel: () => {
                        navigate(URLS.osci0203031s + "/" + data.appInfo?.content?.appFmId);
                    },
                });
            },
            onError: (error) => {
                console.log(error);
                let errMsg = error?.response?.data?.errorMessage;
                if (!errMsg) {
                    errMsg = "msg.osc.00015";
                }
                modal.openModal({
                    content: errMsg,
                });
            },
        }),
        validateHsCodeList: useFetch({
            api: (excelData: any[]) =>
                APIS.validateHsCodeList({
                    ...form.lpcoInfoRgsr.getValues(),
                    hsCdList: [...(grid.ogaHsCdList.getViewData() || [])],
                    excelHsCdList: [...(excelData || [])],
                }),
            onSuccess: (data) => {
                const preValdnResDto = data?.hsCdInfo?.content?.preValdnResDto || {};
                const errList = preValdnResDto["errList"] || [];
                if (errList.length > 0) {
                    modal.openModal({
                        content: <CommonErrors errors={preValdnResDto.errList} type="preValid" />,
                        draggable: true,
                        size: "xl",
                    });
                } else {
                    const excelHsCdList = data?.hsCdInfo?.content?.excelHsCdList || [];
                    for (const rowData of excelHsCdList) {
                        grid.ogaHsCdList.addRow(rowData);
                    }
                }
            },
            onError: (error) => {
                console.log(error);
                const errmsg = error.response.data.errorMessage ? error.response.data.errorMessage : "msg.osc.00015";

                modal.openModal({
                    content: errmsg,
                });
            },
        }),
        getOrgLpcoByPrcsList: useFetch({
            api: (data) => {
                return lpcoUtils.getOrgLpcoByPrcsList(data);
            },
            onSuccess: (data) => {
                console.log("getOrgLpcoByPrcsList onSuccess ", data);
                setOrgLpcoList(data || []);
            },
            onError: () => {},
            showToast: false,
        }),
        getAppDtl: useFetch({
            api: (appCdNo) => {
                return APIS.getAppDtlByAppCdNo(appCdNo);
            },
            onSuccess: (data) => {
                console.log("getOrgLpcoByPrcsList onSuccess ", data);
                console.log("getAppDtl onSuccess ==================== ", data);
                form.lpcoInfoRgsr.setValues(data.appInfo.content?.info);
                setOgaHsCdList(comnUtils.getGridData(data.appInfo.content?.hsCdList || []));

                console.log("form.lpcoInfoRgsr ", form.lpcoInfoRgsr.getValues());
            },
            onError: () => {},
            showToast: false,
        }),
    };
    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Save Attachments !== OGA HS Code 저장 ==!
        saveOgaHscdList: form.lpcoInfoRgsr.handleSubmit(
            () => {
                // grid validate
                let errResult = grid.ogaHsCdList.validate() || {};
                errResult["errors"] = errResult?.errors || [];

                // Duplicate Check
                const ogaHsCdList = grid.ogaHsCdList.getViewData();
                const duplMap: { [key: string]: boolean } = {};
                let idx = 1;
                for (const item of ogaHsCdList) {
                    const hsCd = item.hsCd;
                    if (hsCd && duplMap[hsCd]) {
                        errResult.errors.push(lpcoUtils.getErrorObj(idx, "L_HS_CD", "msg.osc.00017", hsCd));
                    } else {
                        duplMap[hsCd] = true;
                    }
                    idx++;
                }
                if (errResult && errResult.errors?.length > 0) {
                    // sort by row
                    errResult.errors = lpcoUtils.errorListSortByRow(errResult.errors);
                    // error layer popup
                    modal.openModal({
                        content: <CommonErrors {...errResult} />,
                        size: "lg",
                        draggable: true,
                    });
                } else {
                    const data = { ...form.lpcoInfoRgsr.getValues(), hsCdList: grid.ogaHsCdList.getData() };
                    modal.openModal({
                        content: "msg.osc.00090",
                        onConfirm: () => {
                            fetch.saveOgaHscdList.fetch(data);
                        },
                    });
                }
            },
            // () => {
            //     toast.showToast({ type: "warning", content: "msg.osc.00003" });
            // },
        ),
        validateHsCodeList: (excelData: any[]) => {
            // !== LIST 데이터 업로드 후 중복 체크 및 OGA HS CODE 인지 체크  ==!
            fetch.validateHsCodeList.fetch(excelData);
        },
        getAppDtl: (appCdNo: any) => {
            console.log("getAppDtl ", appCdNo);
            fetch.getAppDtl.fetch(appCdNo);
        },
        click_Btn_AddGridList: () => {
            form.lpcoInfoRgsr.handleSubmit(() => {
                grid.ogaHsCdList.addRow({ rqrdInpYn: "N" });
            })();
        },
        click_Btn_DeleteGridList: () => {
            const seltList: any[] = grid.ogaHsCdList.getChecked() || [];
            if (comnUtils.isEmpty(seltList)) {
                modal.openModal({ content: "msg.osc.00009" });
                return;
            }

            grid.ogaHsCdList.deleteRow("checkbox");

            // modal.openModal({
            //     content: "msg.osc.00095",
            //     onConfirm: () => {
            //         grid.ogaHsCdList.deleteRow("checkbox");
            //     },
            // });
        },

        setInit: async () => {
            // 기관 LPCO SELECT 목록
            fetch.getOrgLpcoByPrcsList.fetch(auth.get("orgCd"));
        },
    };

    useEffect(() => {
        handler.setInit();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_LPCO_APP_INFO"} titleSize={2}></Group.Title>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoRgsr.schema.orgNm}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.lpcoInfoRgsr.schema.appCdNo}
                                options={orgLpcoList}
                                onChange={(ev: any) => {
                                    console.log("change ev : ", ev);
                                    if (ev) {
                                        handler.getAppDtl(ev);
                                    } else {
                                        grid.ogaHsCdList.clearData();
                                        form.lpcoInfoRgsr.setValue("appTpCd", "");
                                    }
                                }}
                            ></Group.Control>
                            <Group.Control {...form.lpcoInfoRgsr.schema.appTpCd}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_HS_CD_LST"} titleSize={2}></Group.Title>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                variant="outline-secondary"
                                onClick={() => {
                                    lpcoUtils.saveExcelDownload(
                                        process.env.REACT_APP_API_OSCI +
                                            "/api/v1/osci/lpco/cfgn/hscd/oga-hs/excel-down",
                                        {},
                                    );
                                }}
                            >
                                {t("L_OGA_HS_EXCL_DWNL")}
                            </Button>

                            <Button
                                role="tmplDown"
                                onClick={() => {
                                    lpcoUtils.saveExcelDownload(
                                        process.env.REACT_APP_API_OSCI + "/api/v1/osci/lpco/cfgn/hscd/excel-template",
                                        {},
                                    );
                                }}
                            />
                            <Button
                                role="excelUpload"
                                onClick={async () => {
                                    const formValid = await form.lpcoInfoRgsr.validate();
                                    if (formValid) {
                                        const excelData =
                                            (await grid.ogaHsCdList.importExcel({
                                                headers: 1,
                                                bindings: {
                                                    A: "hsCd",
                                                },
                                                sheet: 0,
                                            })) || [];
                                        //시트명, 헤더 ROW 수
                                        // for (let rowData of excelData) {
                                        //     grid.ogaHsCdList.addRow(rowData);
                                        // }
                                        // !== 서버로 업로드 후 OGA HS CODE인지 , HS DESCRIPTION , VALID DATE 정보 가져와야함. ==!
                                        handler.validateHsCodeList(excelData);
                                    }
                                }}
                            ></Button>
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role="gridAdd"
                                onClick={() => {
                                    handler.click_Btn_AddGridList();
                                }}
                            ></Button>
                            <Button
                                role="gridDelete"
                                onClick={() => {
                                    handler.click_Btn_DeleteGridList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                    {/*
                     * 그리드
                     * @ 그리드 스키마 주입 : {...grid.[그리드이름].grid}
                     * @ 데이터 data={fetch.[fetch 명].data?.[api 리턴 vo 명]}
                     * @ 셀클릭이벤트 연결 : onCellClick={handler.[그리드 이벤트 핸들러명]}
                     */}
                    <Grid
                        {...grid.ogaHsCdList.grid}
                        data={ogaHsCdList} /* fetch.getRpckItmAppList.data?.ogaHsCdList */
                        render={render.grid_ogaHsCdList}
                        // onCellClick={handler.click_Grid_RpckItmAppList}
                        // onRowClick={handler.click_GridRow_RpckItmAppList}
                    />
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0203030q);
                        }}
                    ></Button>
                    <Button
                        role="reset"
                        onClick={() => {
                            form.lpcoInfoRgsr.reset({ ...lpcoUtils.orgInfo });
                            grid.ogaHsCdList.clearData();
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button role="save" onClick={handler.saveOgaHscdList}></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
